import { ChartDataPoint } from 'features/systemLens'
import {
  calculateOverallRangeWithBuffer,
  computeMultipleScaleRanges,
  computeSingleScaleRanges,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { getAllActiveItems } from 'features/timeSeries/utils/timeSeriesUtils'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  BaseSensorEntry,
  SensorAxisAssignment,
  SensorAxisMapping,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

interface UseTimeSeriesUXProps {
  selectedSensors: BaseSensorEntry[]
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  unitMapping: Record<string, string>
  chartData: ChartDataPoint[]
}

export const useTimeSeriesUX = ({
  selectedSensors,
  derivedValues,
  unitMapping,
  chartData,
}: UseTimeSeriesUXProps) => {
  const [expandedAxes, setExpandedAxes] = useState<Record<string, boolean>>({})
  const [xAxisSensorId, setXAxisSensorId] = useState<string | null>(null)

  const [singleScaleMode, setSingleScaleMode] = useState(false)
  const [autoScaleMode, setAutoScaleMode] = useState(true)
  const [customAxisRanges, setCustomAxisRanges] = useState<
    Record<string, { min?: number; max?: number }>
  >({})
  const [customXAxisRange, setCustomXAxisRange] = useState<{
    min?: number
    max?: number
  }>({})
  const [sensorAxisAssignment, setSensorAxisAssignment] =
    useState<SensorAxisAssignment>({})
  const [highlightedSensorId, setHighlightedSensorId] = useState<string | null>(
    null
  )

  const sensorRangesCustomScaleCache = useRef<Record<
    string,
    { min: number; max: number; unit: string }
  > | null>(null)

  const sensorRanges = useMemo(() => {
    if (!autoScaleMode && sensorRangesCustomScaleCache.current) {
      return sensorRangesCustomScaleCache.current
    }

    const sensorData: Record<
      string,
      { min: number; max: number; unit: string }
    > = {}

    const allItems = getAllActiveItems(selectedSensors, derivedValues)

    allItems.forEach((item) => {
      if (item) {
        const itemName = (item.name as string) || item.id
        const unit = item.id.startsWith('derived_')
          ? ''
          : unitMapping[itemName] || 'No Unit'
        sensorData[item.id] = { min: Infinity, max: -Infinity, unit }
      }
    })

    if (chartData.length > 0) {
      allItems.forEach((item) => {
        if (item && sensorData[item.id]) {
          chartData.forEach((point) => {
            const value = point[item.id]
            if (typeof value === 'number' && !isNaN(value)) {
              sensorData[item.id].min = Math.min(sensorData[item.id].min, value)
              sensorData[item.id].max = Math.max(sensorData[item.id].max, value)
            }
          })
        }
      })
    }

    if (singleScaleMode && allItems.length > 0) {
      const overallRange = calculateOverallRangeWithBuffer(allItems, sensorData)

      if (overallRange) {
        const { bufferedMin, bufferedMax } = overallRange
        allItems.forEach((item) => {
          if (sensorData[item.id]) {
            sensorData[item.id].min = bufferedMin
            sensorData[item.id].max = bufferedMax
          }
        })
      }
    } else {
      // Multiple scale mode - apply buffer to each sensor individually
      allItems.forEach((item) => {
        if (sensorData[item.id]) {
          const data = sensorData[item.id]
          if (isFinite(data.min) && isFinite(data.max)) {
            const buffer = (data.max - data.min) * 0.1
            sensorData[item.id].min = data.min - buffer
            sensorData[item.id].max = data.max + buffer
          }
        }
      })
    }

    sensorRangesCustomScaleCache.current = sensorData
    return sensorData
  }, [
    selectedSensors,
    derivedValues,
    singleScaleMode,
    unitMapping,
    autoScaleMode,
    chartData,
  ])

  const handleAutoScaleModeToggle = useCallback(
    (newAutoScaleMode: boolean) => {
      if (!newAutoScaleMode) {
        // Switching from auto to custom - populate customAxisRanges with current computed values
        const allItems = getAllActiveItems(selectedSensors, derivedValues)

        const newRanges = singleScaleMode
          ? computeSingleScaleRanges(allItems, sensorRanges)
          : computeMultipleScaleRanges(allItems, chartData, sensorRanges)

        setCustomAxisRanges(newRanges)
      } else {
        // Switching from custom to auto - clear customAxisRanges
        setCustomAxisRanges({})
      }
      setAutoScaleMode(newAutoScaleMode)
    },
    [singleScaleMode, selectedSensors, derivedValues, sensorRanges, chartData]
  )

  const handleScaleModeToggle = useCallback(
    (newSingleScaleMode: boolean) => {
      if (!autoScaleMode) {
        const allItems = getAllActiveItems(selectedSensors, derivedValues)

        setCustomAxisRanges((prevRanges) => {
          const newRanges = newSingleScaleMode
            ? computeSingleScaleRanges(allItems, sensorRanges, prevRanges)
            : computeMultipleScaleRanges(allItems, chartData, sensorRanges)

          return newRanges
        })
      }
      setSingleScaleMode(newSingleScaleMode)
    },
    [autoScaleMode, selectedSensors, derivedValues, sensorRanges, chartData]
  )

  const handleAxisRangeChange = useCallback(
    (sensorId: string, value: string, type: 'max' | 'min') => {
      if (autoScaleMode) {
        return
      }

      const numValue = value ? parseFloat(value) : undefined

      setCustomAxisRanges((prev) => {
        if (singleScaleMode) {
          const allItems = getAllActiveItems(selectedSensors, derivedValues)
          const newRanges = { ...prev }

          allItems.forEach((item) => {
            newRanges[item.id] = {
              ...prev[item.id],
              [type]: numValue,
            }
          })

          newRanges['single-scale'] = {
            ...prev['single-scale'],
            [type]: numValue,
          }

          return newRanges
        }

        return {
          ...prev,
          [sensorId]: {
            ...prev[sensorId],
            [type]: numValue,
          },
        }
      })
    },
    [autoScaleMode, singleScaleMode, selectedSensors, derivedValues]
  )

  const handleYMinChange = useCallback(
    (sensorId: string, value: string) =>
      handleAxisRangeChange(sensorId, value, 'min'),
    [handleAxisRangeChange]
  )

  const handleYMaxChange = useCallback(
    (sensorId: string, value: string) =>
      handleAxisRangeChange(sensorId, value, 'max'),
    [handleAxisRangeChange]
  )

  const sensorAxisMapping = useMemo(() => {
    const mapping: SensorAxisMapping = {}

    const allItems = getAllActiveItems(selectedSensors, derivedValues)

    // Assign axes to all items (sensors and derived values)
    if (singleScaleMode) {
      // In single scale mode, all items share the same axis
      allItems.forEach(({ id: itemId }) => {
        mapping[itemId] = {
          axis: 'y',
          side: 'left',
          index: 0,
        }
      })
    } else {
      // In multi scale mode, each item gets its own axis
      allItems.forEach(({ id: itemId }, index) => {
        // Check if user has manually assigned this item
        const userAssignment = sensorAxisAssignment[itemId]

        if (userAssignment) {
          mapping[itemId] = {
            axis: index === 0 ? 'y' : `y${index + 1}`,
            side: userAssignment.side,
            index: index,
          }
        } else {
          // Auto-assign alternating between left and right
          const side = index % 2 === 0 ? 'left' : 'right'

          mapping[itemId] = {
            axis: index === 0 ? 'y' : `y${index + 1}`,
            side,
            index: index,
          }
        }
      })
    }

    return mapping
  }, [selectedSensors, derivedValues, singleScaleMode, sensorAxisAssignment])

  return {
    // Chart display settings
    singleScaleMode,
    setSingleScaleMode,
    autoScaleMode,
    setAutoScaleMode,
    expandedAxes,
    setExpandedAxes,
    xAxisSensorId,
    setXAxisSensorId,
    // Axis/scale management
    customAxisRanges,
    setCustomAxisRanges,
    customXAxisRange,
    setCustomXAxisRange,
    sensorAxisAssignment,
    setSensorAxisAssignment,
    sensorAxisMapping,
    handleScaleModeToggle,
    handleAutoScaleModeToggle,
    handleAxisRangeChange,
    handleYMinChange,
    handleYMaxChange,
    sensorRanges,
    highlightedSensorId,
    setHighlightedSensorId,
  }
}
